import { Control, Controller, Path } from 'react-hook-form';
import { Autocomplete, TextField } from '@mui/material';
import { queries } from 'RestAPIs/rapini';
import type { DutyCode } from 'generated';

type Props<FormValues extends Record<string, any>> = {
  control: Control<FormValues>;
  name: Path<FormValues>;
  label: string;
  referenceDate?: Date;
  organizationalUnitId?: number;
  includeRequestExcluded?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  required?: boolean;
};

export default function ControlledDutyCodeSelectorBase<
  FormValues extends Record<string, any>
>({
  control,
  name,
  label,
  referenceDate,
  disabled,
  fullWidth,
  organizationalUnitId,
  includeRequestExcluded,
  required
}: Props<FormValues>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { data: dutyCodes = [], isLoading } = queries.useGetDutyCodes(
          referenceDate?.toISOString(),
          organizationalUnitId ?? undefined,
          includeRequestExcluded ?? undefined
        );

        const existingValue = dutyCodes.find(
          (dutyCode) => dutyCode.dutyCodeId === field.value
        );

        return (
          <Autocomplete<DutyCode, false, false, false>
            options={dutyCodes}
            loading={isLoading}
            value={existingValue ?? field.value ?? null}
            onChange={(_, dutyCode) => field.onChange(dutyCode)}
            getOptionLabel={(dutyCode) =>
              dutyCode?.code || dutyCode?.dutyCode || ''
            }
            isOptionEqualToValue={(a, b) => a?.dutyCodeId === b?.dutyCodeId}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant="outlined"
                size="small"
                fullWidth={fullWidth}
                required={required}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        );
      }}
    />
  );
}
